import AboutUsImageAndText from 'components/about-us/AboutUsImageAndText';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AboutUsSplitHero from 'components/about-us/AboutUsSplitHero';
import AboutUsTextAndCarousel from 'components/about-us/AboutUsTextAndCarousel';
import AboutUsTextModule from 'components/about-us/AboutUsTextModule';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import MainLayout from 'components/layout/MainLayout';
import RoundedButton from 'components/RoundedButton';
import { teamMemberCount, teamMembers } from 'constants/daymadeCompanyInfo';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import Link from 'next/link';
import { Fragment } from 'react';
import { ASSET_PREFIX } from 'utils/constants';
import styles from './about-us.module.css';

const partners = [
  {
    width: 118,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-virgin-experience-days.jpg`,
    text: 'Virgin Experience Days logo',
  },
  {
    width: 107,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-lucky-trip.png`,
    text: 'Lucky Trip logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-lastminute.jpg`,
    text: 'Lastminute logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-lego.png`,
    text: 'Lego logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-charity-shop-gift-card.png`,
    text: 'Charity Shop Gift Card logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-joe-and-sephs.png`,
    text: 'Joe and Sephs logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-buyagift.png`,
    text: 'Buyagift logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-currys.png`,
    text: 'Currys logo',
  },
  {
    width: 117,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-wild.png`,
    text: 'Wild logo',
  },
  {
    width: 118,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-veritree.png`,
    text: 'Veritree logo',
  },
  {
    width: 105,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-ecologi.png`,
    text: 'Ecologi logo',
  },
  {
    width: 91,
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/logo-zeal.png`,
    text: '',
  },
];

const bottomLinks = [
  {
    text: 'Contact\nus',
    href: '/contact-us',
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/bb-contact.jpg`,
    imageAlt: 'A photo of the team',
  },
  {
    text: 'Our\nwinners',
    href: '/winners',
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/bb-winners.jpg`,
    imageAlt: 'A photo of our winners',
  },
  {
    text: 'Follow\nus',
    href: 'https://www.instagram.com/daymadeuk/',
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/bb-follow.jpg`,
    imageAlt: 'A photo of a phone',
  },
  {
    text: 'FAQ’s',
    href: '/faq',
    imagePath: `${ASSET_PREFIX}/images/about-us/2025/bb-faqs.jpg`,
    imageAlt: 'Some people celebrating',
  },
];

const AboutUs = () => {
  return (
    <MainLayout limitItemWidths>
      <Head>
        <link
          href="https://fonts.googleapis.com/css?family=Satisfy&display=swap"
          rel="stylesheet"
        />
      </Head>

      <NextSeo
        title="Win Holidays Online | About Us | Prize Platform | DAYMADE"
        description="Play with DAYMADE for your chance to win holidays online. Become a winner from the comfort of your home. Find out about us, our prizes, and what we do here."
        openGraph={{
          title: 'Win Holidays Online | About Us | Prize Platform | DAYMADE',
          description:
            'Play with DAYMADE for your chance to win holidays online. Become a winner from the comfort of your home. Find out about us, our prizes, and what we do here.',
        }}
      />
      <div className={styles.fixedHeaderHeightFudge}></div>

      <div className="aboutUsTypographyReset">
        <AboutUsSplitHero
          imagePath={`${ASSET_PREFIX}/images/about-us/2025/callum-draw.jpg`}
          imageAlt="Callum drawing the lottery results"
        >
          <h1 className="aboutUsTypographyHeaderXlargeOnTeal">
            A little bit more about DAYMADE
          </h1>
          <p>
            Who are we? It's all in our name! We make people's day, every day.
            With our prize draws, everybody has a chance to get surprised with a
            little (or big!) win while planting trees. From comedy nights,
            travel mugs, IKEA gift cards and country manor stays all the way up
            to full home renovations and bucket-list holidays all across the
            globe, the opportunities are well and truly endless!
          </p>
          <p>
            <RoundedButton
              href="/how-it-works"
              rebrandDm2025
              colorStyle="OutlineDark"
            >
              How it works
            </RoundedButton>
          </p>
        </AboutUsSplitHero>

        <AboutUsTextModule>
          <p>
            Our mission is to spread happiness, positivity, and possibility,
            while supporting planet reforestation with every purchase.
          </p>
        </AboutUsTextModule>

        <AboutUsImageAndText
          background="white"
          images={[
            {
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/founders.jpg`,
              imageAlt: 'The DAYMADE founders',
              verticalFocus: 'top',
            },
          ]}
          showImageFirstOnMobile
          header={
            <h2 className="aboutUsTypographyHeaderXlargeOnWhite">
              Meet the founders
            </h2>
          }
        >
          <p>
            DAYMADE was founded by Lisa Nederveen, Callum Wright, and Andrew
            Broekelmann back in 2019. We wanted to create a new type of prize
            draw with smaller and more attainable non-cash, experience-led
            prizes to create better odds for our players and a more fun & fair
            playing field.
          </p>

          <p>
            Working with small and sustainable prize partners across the UK as
            well as our charity partners tackling climate change means there's a
            win-win model across our entire product that we are very proud
            about.
          </p>

          <p>
            Fast forward to 2025 and DAYMADE has grown into a London-based team
            of {teamMemberCount} people who are all dedicated to leaving you
            with a great experience and winning memories to last a lifetime!
          </p>
        </AboutUsImageAndText>

        <AboutUsTextAndCarousel
          background="tealGradient"
          items={[
            {
              title: '2019',
              text: 'The launch of ‘TripHunters’',
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/got-here-2019.jpg`,
              imageAlt: 'The team around a lottery machine',
            },
            {
              title: '2020',
              text: 'We introduce our first non-travel prizes',
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/got-here-2020.jpg`,
              imageAlt: 'Some food and a games console',
            },
            {
              title: '2021',
              text: 'Rebrand time (thanks COVID)',
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/got-here-2021.jpg`,
              imageAlt: 'A person celebrating',
            },
            {
              title: '2023',
              text: 'We plant our 1,000,000th tree',
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/giving-back.jpg`,
              imageAlt: 'Some people celebrating a tree being planted',
            },
            {
              title: '2024',
              text: 'We award our 68,000th prize',
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/got-here-2024.jpg`,
              imageAlt: 'A happy winner',
            },
            {
              title: '2025',
              text: 'The team grows to 15 people (from 2 😵)',
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/got-here-2025.jpg`,
              imageAlt: 'A photo of the team',
            },
          ]}
        >
          <h2 className="aboutUsTypographyHeaderXlargeOnTeal">
            How we got here...
          </h2>
          <p className="aboutUsTypographyLimitedWidthCentered">
            We’ve been on quite a journey since we launched in 2019! What
            started as an idea on a post-it note has become DAYMADE as we know
            it today and what an adventure it has been!
          </p>
        </AboutUsTextAndCarousel>

        <AboutUsImageAndText
          background="grey"
          images={[
            {
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/community1.jpg`,
              imageAlt: 'Our winners',
              verticalFocus: 'bottom',
            },
            {
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/community2.jpg`,
              imageAlt: 'Our winners',
            },
            {
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/community3.jpg`,
              imageAlt: 'Our winners',
              verticalFocus: 'bottom',
            },
            {
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/community4.jpg`,
              imageAlt: 'Our winners',
              verticalFocus: 'top',
            },
          ]}
          imageOnRight
          header={
            <h2 className="aboutUsTypographyHeaderXlargeOnWhite">
              Our community
            </h2>
          }
        >
          <p>
            Over the last years we've created a vibrant community of like-minded
            individuals who share a passion for travel, the environment and
            making the most of every day. Many of the prizes featured on our
            site have come recommended by our players. Some are even hand-made
            by our players such as Artisan Cheltenham candles and Naturally
            Legit skincare.
          </p>
          <p>
            Our London-based customer experience team consisting of Violet,
            Jordan, Heenal & Sakinah work hard every day to get our winners’
            prizes posted and trips & experiences booked! These include
            Harriet's train journey through Japan, Sarah's water bungalow stay
            in the Maldives, Ross' magical Disney holiday (where he proposed to
            his boyfriend!) and Lorna's cruise through Norway. You can hear more
            from our winners on our winners page and Instagram feed.
          </p>
        </AboutUsImageAndText>

        <AboutUsImageAndText
          background="grey"
          images={[
            {
              imagePath: `${ASSET_PREFIX}/images/about-us/2025/giving-back.jpg`,
              imageAlt: 'People celebrating',
              verticalFocus: 'top',
            },
          ]}
          header={
            <h2 className="aboutUsTypographyHeaderXlargeOnWhite aboutUsTypographyAltUnderlineColour">
              Giving back
            </h2>
          }
        >
          <p>
            With DAYMADE, your subscription helps our holistic approach to
            sustainability.
          </p>
          <p>
            Not only does every purchase made on our site plants a tree with
            Veritree, we also work with a lot of planet-friendly partners and
            carbon off-set all our winners' flights as well as our team's carbon
            footprint through our UK-based partner, Ecologi.
          </p>
          <p>
            <RoundedButton
              href="giving-back"
              rebrandDm2025
              colorStyle="OutlineDark"
            >
              Discover more
            </RoundedButton>
          </p>
        </AboutUsImageAndText>

        <AboutUsModule background="white">
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlargeOnWhite">
              Meet the team
            </h2>
            <p className="aboutUsTypographyCentered">
              We are a team of {teamMemberCount} travel-lovers based in London
              who work tirelessly to deliver a great experience. We are also
              backed by Zeal Network - a publicly traded company in the gaming
              space. For questions, you can reach us anytime!
            </p>
            <div className={styles.teamContainer}>
              {teamMembers.map((x) => (
                <div className={styles.teamMember}>
                  <AspectRatioContainer
                    width={1}
                    height={1}
                    innerClassName={styles.teamMemberShadow}
                  >
                    <img
                      className={styles.teamMemberImage}
                      src={x.image}
                      alt={`Photo of ${x.name}`}
                    />
                    <img
                      className={styles.teamMemberFunImage}
                      src={x.funImage}
                      alt={`Fun photo of ${x.name}`}
                    />
                    <div className={styles.teamMemberDetails}>
                      <strong>{x.name}</strong>
                      <p>{x.jobTitle}</p>
                    </div>
                    {x.linkedInLink && (
                      <a
                        className={styles.teamMemberLinkedIn}
                        href={x.linkedInLink}
                        target="_blank"
                      >
                        <img
                          src={`${ASSET_PREFIX}/images/about-us/2025/linked-in-white-circle.svg`}
                        />
                        <span className="visuallyHidden">LinkedIn profile</span>
                      </a>
                    )}
                  </AspectRatioContainer>
                </div>
              ))}
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="tealGradient">
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlargeOnTeal">
              Our partners
            </h2>
            <p className="aboutUsTypographyCentered">
              In addition to making our players’ days every week, we offer a
              platform for prize partners (including small businesses, b-corps
              and established brands) like these to reach a new young audience
              and weekly business.
            </p>
            <div className={styles.partnerLogosContainer}>
              {partners.map((x, index) => (
                <div key={index} className={styles.partnerLogoContainer}>
                  <img width={x.width} src={x.imagePath} alt={x.text} />
                </div>
              ))}
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white" verticalPadding={false}>
          <AboutUsSiteContainer wider>
            <div className={styles.bottomLinksPanel}>
              {bottomLinks.map((x) => (
                <div className={styles.bottomLinksPanelItem}>
                  <Link href={x.href} legacyBehavior={false}>
                    <span>
                      {x.text.split('\n').map((x, i) =>
                        i ? (
                          <Fragment key={i}>
                            <br />
                            {x}
                          </Fragment>
                        ) : (
                          x
                        ),
                      )}
                    </span>
                  </Link>
                  <img src={x.imagePath} alt={x.imageAlt} />
                </div>
              ))}
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>
      </div>
    </MainLayout>
  );
};

export default AboutUs;
